import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  ClinicalEncounter,
  CreateProblemInput,
  Problem,
  SoapNote,
  StartClinicalEncounterInput,
  UpdateSoapInput,
} from '@/types/api';

export function useStartClinicalEncounter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: StartClinicalEncounterInput) =>
      api.post<ClinicalEncounter>('/api/consultas-clinicas/iniciar', input),
    onSuccess: (encounter) => {
      queryClient.setQueryData(['clinical-encounter', encounter.id], encounter);
      queryClient.invalidateQueries({ queryKey: ['problems', encounter.cdPaciente] });
      queryClient.invalidateQueries({ queryKey: ['agenda-medico'] });
    },
  });
}

export function useClinicalEncounter(
  id: string | null,
): UseQueryResult<ClinicalEncounter, Error> {
  return useQuery({
    queryKey: ['clinical-encounter', id],
    queryFn: ({ signal }) =>
      api.get<ClinicalEncounter>(`/api/consultas-clinicas/${id}`, { signal }),
    enabled: !!id,
  });
}

export function usePatientClinicalEncounters(cdPaciente: number | null): UseQueryResult<ClinicalEncounter[], Error> {
  return useQuery({
    queryKey: ['clinical-encounters', cdPaciente],
    queryFn: ({ signal }) => api.get<ClinicalEncounter[]>(`/api/consultas-clinicas/pacientes/${cdPaciente}/historico-pep`, { signal }),
    enabled: !!cdPaciente,
  });
}

export function useUpdateSOAP(encounterId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateSoapInput) =>
      api.put<SoapNote>(`/api/consultas-clinicas/${encounterId}/soap`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinical-encounter', encounterId] });
    },
  });
}

export function useFinishClinicalEncounter(encounterId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.post<ClinicalEncounter>(`/api/consultas-clinicas/${encounterId}/finalizar`, {}),
    onSuccess: (encounter) => {
      queryClient.setQueryData(['clinical-encounter', encounter.id], encounter);
      queryClient.invalidateQueries({ queryKey: ['agenda-medico'] });
    },
  });
}

export function useProblems(cdPaciente: number | null): UseQueryResult<Problem[], Error> {
  return useQuery({
    queryKey: ['problems', cdPaciente],
    queryFn: ({ signal }) =>
      api.get<Problem[]>(
        `/api/consultas-clinicas/pacientes/${cdPaciente}/problemas`,
        { signal },
      ),
    enabled: !!cdPaciente,
    staleTime: 30_000,
  });
}

export function useCreateProblem(cdPaciente: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateProblemInput) =>
      api.post<Problem>(
        `/api/consultas-clinicas/pacientes/${cdPaciente}/problemas`,
        input,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['problems', cdPaciente] });
    },
  });
}

export function useInactivateProblem(cdPaciente: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (problemId: string) =>
      api.patch<Problem>(`/api/consultas-clinicas/problemas/${problemId}/inativar`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['problems', cdPaciente] });
    },
  });
}

export function useActiveProblemCatalog() {
  return useQuery({
    queryKey: ['problem-catalog', 'active'],
    queryFn: ({ signal }) => api.get<Array<{ id: string; name: string; code?: string | null; category?: string | null }>>('/api/admin/document-templates/problems/active', { signal }),
    staleTime: 60_000,
  });
}
